import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  CircularProgress,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Close,
  AccountBalance,
  Save,
} from '@mui/icons-material';
import { PaymentLink, PaymentMethod, Currency, Payment } from '../../types/PaymentLink';
import { addManualPayment, editManualPayment } from '../../services/paymentLinkService';
import { toast } from 'react-toastify';

interface AddManualPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  paymentLink: PaymentLink | null;
  mode?: 'add' | 'edit';
  paymentToEdit?: Payment | null;
  onSuccess: () => void;
}

interface FormData {
  payerName: string;
  amount: string;
  paymentDate: Date;
  paymentMethod: PaymentMethod;
}

interface FormErrors {
  payerName?: string;
  amount?: string;
  paymentDate?: string;
  paymentMethod?: string;
}

// Payment method options with labels
const PAYMENT_METHODS: { value: PaymentMethod; label: string; labelTr: string }[] = [
  { value: 'cash', label: 'Cash', labelTr: 'Nakit' },
  { value: 'credit_card', label: 'Credit Card (Manual Entry)', labelTr: 'Kredi Kartı (Manuel)' },
  { value: 'bank_transfer', label: 'Bank Transfer (EFT/Havale)', labelTr: 'Havale/EFT' },
  { value: 'wire_transfer', label: 'Wire Transfer', labelTr: 'Banka Havalesi' },
  { value: 'belek', label: 'Belek', labelTr: 'Belek' },
  { value: 'other', label: 'Other', labelTr: 'Diğer' },
];

// Currency symbols
const CURRENCY_SYMBOLS: Record<Currency, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  TRY: '₺',
};

const AddManualPaymentDialog: React.FC<AddManualPaymentDialogProps> = ({
  open,
  onClose,
  paymentLink,
  mode = 'add',
  paymentToEdit = null,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>({
    payerName: '',
    amount: '',
    paymentDate: new Date(),
    paymentMethod: 'cash',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);

  const isEditMode = mode === 'edit' && !!paymentToEdit;

  React.useEffect(() => {
    if (!open || !paymentLink) return;

    toast.info(
      isEditMode
        ? 'Manual payment edit mode active'
        : 'Manual payment add mode active',
      {
        autoClose: 1800,
        toastId: isEditMode
          ? `manual-payment-edit-tip-${paymentToEdit?.id || 'unknown'}`
          : `manual-payment-add-tip-${paymentLink.id}`,
      }
    );
  }, [open, paymentLink, isEditMode, paymentToEdit]);

  // Reset form when dialog opens with a new payment link
  React.useEffect(() => {
    if (open && paymentLink) {
      if (isEditMode && paymentToEdit) {
        setFormData({
          payerName: paymentToEdit.cardHolderName || paymentLink.customerName || '',
          amount: paymentToEdit.amount.toString(),
          paymentDate: paymentToEdit.timestamp.toDate(),
          paymentMethod: paymentToEdit.paymentMethod || 'cash',
        });
      } else {
        setFormData({
          payerName: paymentLink.customerName || '',
          amount: '',
          paymentDate: new Date(),
          paymentMethod: 'cash',
        });
      }
      setErrors({});
    }
  }, [open, paymentLink, isEditMode, paymentToEdit]);

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = event.target.value ? new Date(event.target.value) : new Date();
    setFormData({
      ...formData,
      paymentDate: dateValue,
    });

    if (errors.paymentDate) {
      setErrors({
        ...errors,
        paymentDate: undefined,
      });
    }
  };

  const getMaxAllowedAmount = (): number => {
    if (!paymentLink) return 0;
    if (!isEditMode || !paymentToEdit) {
      return paymentLink.remainingAmount;
    }
    return paymentLink.remainingAmount + paymentToEdit.amount;
  };

  const validateForm = (): boolean => {
    if (!paymentLink) return false;
    if (isEditMode && !paymentToEdit) return false;

    const newErrors: FormErrors = {};
    const maxAllowedAmount = getMaxAllowedAmount();

    // Validate payer name
    if (!formData.payerName.trim()) {
      newErrors.payerName = 'Payer name is required';
    } else if (formData.payerName.trim().length < 2) {
      newErrors.payerName = 'Name must be at least 2 characters';
    }

    // Validate amount
    if (!formData.amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else {
      const amountNum = parseFloat(formData.amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        newErrors.amount = 'Amount must be greater than 0';
      } else if (amountNum > maxAllowedAmount) {
        newErrors.amount = `Amount cannot exceed allowed maximum (${maxAllowedAmount.toFixed(2)})`;
      }
    }

    // Validate payment date
    if (!formData.paymentDate) {
      newErrors.paymentDate = 'Payment date is required';
    }

    // Validate payment method
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Payment method is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !paymentLink) {
      return;
    }

    setLoading(true);

    try {
      if (isEditMode && paymentToEdit) {
        await editManualPayment({
          paymentLinkId: paymentLink.id,
          paymentId: paymentToEdit.id,
          payerName: formData.payerName.trim(),
          amount: parseFloat(formData.amount),
          paymentDate: formData.paymentDate,
          paymentMethod: formData.paymentMethod,
        });
        toast.success('Manual payment updated successfully!');
      } else {
        await addManualPayment({
          paymentLinkId: paymentLink.id,
          payerName: formData.payerName.trim(),
          amount: parseFloat(formData.amount),
          paymentDate: formData.paymentDate,
          paymentMethod: formData.paymentMethod,
        });
        toast.success('Manual payment added successfully!');
      }

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Error saving manual payment:', error);
      toast.error(error.message || 'Failed to save manual payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        payerName: '',
        amount: '',
        paymentDate: new Date(),
        paymentMethod: 'cash',
      });
      setErrors({});
      onClose();
    }
  };

  if (!paymentLink) return null;
  if (mode === 'edit' && !paymentToEdit) return null;

  const currencySymbol = CURRENCY_SYMBOLS[paymentLink.currency] || paymentLink.currency;
  const maxAllowedAmount = getMaxAllowedAmount();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
          color: 'white',
        }}
      >
        <AccountBalance />
        <Typography variant="h6" component="span">
          {isEditMode ? 'Edit Manual Payment' : 'Add Manual Payment'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isEditMode
              ? 'Update this manual payment record. Changes are audited under the payment link.'
              : 'Record offline payment details for this invoice.'}
          </Typography>

          {/* Payment Link Info */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.100',
              borderRadius: 1,
              mb: 3,
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Customer
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {paymentLink.customerName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Invoice Amount
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {currencySymbol}{paymentLink.invoiceAmount.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Total Paid
                </Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">
                  {currencySymbol}{paymentLink.totalPaid.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Remaining Balance
                </Typography>
                <Typography variant="body2" fontWeight={600} color="warning.main">
                  {currencySymbol}{paymentLink.remainingAmount.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Grid container spacing={2.5}>
          {/* Payer Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Payer Name"
              value={formData.payerName}
              onChange={handleChange('payerName')}
              error={!!errors.payerName}
              helperText={errors.payerName || 'Name of the person who made the payment'}
              required
              disabled={loading}
              placeholder="Enter payer name"
            />
          </Grid>

          {/* Amount */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={handleChange('amount')}
              error={!!errors.amount}
              helperText={errors.amount || `Max: ${currencySymbol}${maxAllowedAmount.toFixed(2)}`}
              required
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {currencySymbol}
                  </InputAdornment>
                ),
              }}
              inputProps={{
                min: 0.01,
                max: maxAllowedAmount,
                step: 0.01,
              }}
            />
          </Grid>

          {/* Payment Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Payment Date"
              type="date"
              value={formData.paymentDate.toISOString().split('T')[0]}
              onChange={handleDateChange}
              error={!!errors.paymentDate}
              helperText={errors.paymentDate || 'Date the payment was received'}
              required
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Payment Method */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Payment Method"
              value={formData.paymentMethod}
              onChange={handleChange('paymentMethod')}
              error={!!errors.paymentMethod}
              helperText={errors.paymentMethod || 'How the payment was received'}
              required
              disabled={loading}
            >
              {PAYMENT_METHODS.map((method) => (
                <MenuItem key={method.value} value={method.value}>
                  {method.label} ({method.labelTr})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          startIcon={<Close />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : (isEditMode ? <Save /> : <Add />)}
          sx={{
            background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
          }}
        >
          {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Payment' : 'Add Payment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddManualPaymentDialog;
